import React from 'react';
import { T, NUMERIC, balanceColor, money } from './customers.theme';
import { PlusIcon, SearchIcon, PeopleIcon } from './CustomersUI';

/**
 * The left column: every customer, searchable.
 *
 * One row per person, exactly as the phone's CustomerTile reads — name on top,
 * `phone · address` beneath, balance on the right in its own colour. The
 * difference is that tapping one here fills the panel beside it instead of
 * pushing a new screen.
 */

function CustomerRow({ customer, currency, selected, onOpen }) {
  const [hovered, setHovered] = React.useState(false);

  const subtitle = [customer.phone, customer.district].filter(Boolean).join(' · ');

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={customer.name}
      onClick={() => onOpen(customer)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(customer);
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 20px',
        background: selected ? T.selectedBg : hovered ? T.hover : 'transparent',
        // The stripe replaces the tint on the left edge so a selected row is
        // still obvious once the list is scrolled and colours repeat.
        borderLeft: `3px solid ${selected ? T.brand : 'transparent'}`,
        borderBottom: `1px solid ${T.hairline}`,
        cursor: 'pointer',
        outline: 'none',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: T.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {customer.name}
        </span>
        <span
          style={{
            fontSize: 12.5,
            color: T.inkFaint,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {subtitle || '—'}
        </span>
      </div>

      <span
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: balanceColor(customer.balance),
          ...NUMERIC,
        }}
      >
        {money(customer.balance, currency)}
      </span>
    </div>
  );
}

export default function CustomerList({
  customers,
  currency,
  query,
  onQueryChange,
  selectedId,
  onOpen,
  onAdd,
  loading,
}) {
  // Only what is owed counts toward the total. A customer in credit is money
  // the shop holds, not money out on the street, and adding the two together
  // would understate the book.
  const owed = customers.reduce((sum, customer) => {
    const balance = Number(customer.balance || 0);
    return balance > 0 ? sum + balance : sum;
  }, 0);

  return (
    <aside
      style={{
        width: T.listWidth,
        flexShrink: 0,
        height: '100%',
        background: T.surface,
        borderRight: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: '20px 20px 16px',
          borderBottom: `1px solid ${T.hairline}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.2, color: T.ink }}>
            Customers
          </span>
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              padding: '0 13px',
              background: T.selectedBg,
              border: `1px solid ${T.selectedBorder}`,
              borderRadius: T.radiusControl,
              font: 'inherit',
              color: T.brandInk,
              cursor: 'pointer',
            }}
          >
            <PlusIcon />
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>Ku Dar Macaamiil</span>
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            height: 40,
            padding: '0 13px',
            border: `1px solid ${T.borderStrong}`,
            borderRadius: T.radiusPill,
            boxSizing: 'border-box',
          }}
        >
          <span style={{ display: 'flex', color: T.inkFaint }}>
            <SearchIcon />
          </span>
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Raadi Macaamiil"
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              font: 'inherit',
              fontSize: 13.5,
              color: T.ink,
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11.5, color: T.inkFaint }}>
            {customers.length} {customers.length === 1 ? 'customer' : 'customers'}
          </span>
          <span style={{ fontSize: 11.5, color: T.inkFaint }}>
            Total owed{' '}
            <strong style={{ fontWeight: 700, color: T.brandInk, ...NUMERIC }}>
              {money(owed, currency)}
            </strong>
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {customers.length ? (
          customers.map((customer) => (
            <CustomerRow
              key={customer._id}
              customer={customer}
              currency={currency}
              selected={customer._id === selectedId}
              onOpen={onOpen}
            />
          ))
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: 40,
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 52,
                height: 52,
                border: `1.5px dashed ${T.borderStrong}`,
                borderRadius: T.radiusPill,
                color: T.inkGhost,
              }}
            >
              <PeopleIcon size={22} />
            </span>
            <span style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.5 }}>
              {loading ? 'Loading…' : 'No customers found'}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}
